/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { LoggerService } from '@shared/services/logger.service';
import { ReportDataSource } from '../reportContent/datasource/report.datasource';

@Injectable()
export class IsolatedReportService {
    public hasData$ = new BehaviorSubject<boolean>(false);
    public hasDataSource$ = new BehaviorSubject<boolean>(false);
    public isVolt$ = new BehaviorSubject<boolean>(false);

    public objectKey: any;

    private _id: string;
    private dataSourceInternal: ReportDataSource;

    private excelTriggerSubject = new Subject<void>();
    private reportTriggerSubject = new Subject<void>();
    private allDataTriggerSubject = new Subject<void>();
    private enableTableEditSubject = new Subject<void>();
    private markChangedSubject = new Subject<void>();

    private containerDimensionSubject = new BehaviorSubject<[number, number]>([0, 0]);
    private contentDimensionSubject = new BehaviorSubject<[number, number]>([0, 0]);

    constructor(private logger: LoggerService) {
    }

    public triggerReport() {
        this.reportTriggerSubject.next();
    }

    public triggerAllData() {
        this.allDataTriggerSubject.next();
    }

    public triggerExcelDownload() {
        this.excelTriggerSubject.next();
    }

    public enableTableEdit() {
        this.enableTableEditSubject.next();
    }

    public markChanged() {
        this.markChangedSubject.next();
    }

    public setHasData(value: boolean) {
        this.hasData$.next(value);
    }

    public setIsVolt(value: boolean) {
        this.isVolt$.next(value);
    }

    public setDigitizeKey(value) {
        this.dataSourceInternal?.setDigitizeKey(value);
    }

    public setContainerDimension(dim: [number, number]) {
        this.containerDimensionSubject.next(dim);
    }

    public setContentDimension(dim: [number, number]) {
        this.contentDimensionSubject.next(dim);
    }

    get id() {
        return this._id;
    }
    set id(value: string) {
        this._id = value;
    }

    get dataSource() {
        return this.dataSourceInternal;
    }
    set dataSource(value: ReportDataSource) {
        this.dataSourceInternal = value;
        this.hasDataSource$.next(true);
    }

    get digitizeKey$() {
        return this.dataSourceInternal?.digitizeKey$
    }

    get containerDimension$() {
        return this.containerDimensionSubject.asObservable();
    }

    get contentDimension$() {
        return this.contentDimensionSubject.asObservable();
    }

    get markChanged$() {
        return this.markChangedSubject.asObservable();
    }

    get enableTableEdit$() {
        return this.enableTableEditSubject.asObservable();
    }

    get allDataTrigger$() {
        return this.allDataTriggerSubject.asObservable();
    }

    get reportTrigger$() {
        return this.reportTriggerSubject.asObservable();
    }

    get excelDownloadTrigger$() {
        return this.excelTriggerSubject.asObservable();
    }
}
